"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, Flame, PhoneCall, Pipette, RotateCcw, ShieldAlert, Clock, ArrowRight, Droplets, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface EmergencyTriageItem {
  id: string;
  title: string;
  description: string;
  urgency: string;
  urgencyLevel: "immediate" | "critical" | "urgent";
  icon: "pipette" | "droplets" | "rotateCcw" | "alert" | "flame" | "wrench";
}
const emergencyItems: EmergencyTriageItem[] = [{
  id: "burst-pipe",
  title: "Burst Pipe Repair",
  description: "Active pressurized flooding causing structural damage. We locate main shutoffs and clamp or replace lines immediately.",
  urgency: "Immediate Dispatch (15-30 Min)",
  urgencyLevel: "immediate",
  icon: "pipette"
}, {
  id: "major-leak",
  title: "Major Water Leak",
  description: "Uncontrolled water pooling inside ceilings, walls, or under floorboards risking foundation and interior damage.",
  urgency: "Immediate Dispatch",
  urgencyLevel: "immediate",
  icon: "droplets"
}, {
  id: "sewer-backup",
  title: "Sewer Backup",
  description: "Hazardous raw sewage coming up through floor drains, tubs, or lower-level toilets presenting biological hazards.",
  urgency: "Critical Response",
  urgencyLevel: "critical",
  icon: "rotateCcw"
}, {
  id: "overflowing-toilet",
  title: "Overflowing Toilet",
  description: "Continuous bowl overflow failing to stop with standard shutoff valves, threatening floor warping and drywall soak.",
  urgency: "Critical Response",
  urgencyLevel: "critical",
  icon: "alert"
}, {
  id: "water-heater-leak",
  title: "Water Heater Leak",
  description: "Ruptured tanks, overheating pressure relief valves, or active basement flooding from failed water heaters.",
  urgency: "Urgent Priority",
  urgencyLevel: "urgent",
  icon: "flame"
}, {
  id: "emergency-drain",
  title: "Emergency Drain Problems",
  description: "Complete main stack blockage backing up all household plumbing fixtures simultaneously.",
  urgency: "Urgent Priority",
  urgencyLevel: "urgent",
  icon: "wrench"
}];
export default function EmergencyServices() {
  const shouldReduceMotion = useReducedMotion();
  const getUrgencyBadge = (level: EmergencyTriageItem["urgencyLevel"]) => {
    if (level === "immediate") {
      return <Badge variant="destructive" className="font-semibold text-xs py-1 px-2.5 uppercase tracking-wide">
          Immediate Action
        </Badge>;
    }
    if (level === "critical") {
      return <Badge className="bg-accent text-accent-foreground font-semibold text-xs py-1 px-2.5 uppercase tracking-wide">
          Critical Issue
        </Badge>;
    }
    return <Badge variant="secondary" className="font-semibold text-xs py-1 px-2.5 uppercase tracking-wide">
        Urgent Priority
      </Badge>;
  };
  const renderIcon = (iconName: EmergencyTriageItem["icon"]) => {
    switch (iconName) {
      case "pipette":
        return <Pipette className="size-6 text-destructive" />;
      case "droplets":
        return <Droplets className="size-6 text-destructive" />;
      case "rotateCcw":
        return <RotateCcw className="size-6 text-destructive" />;
      case "alert":
        return <ShieldAlert className="size-6 text-destructive" />;
      case "flame":
        return <Flame className="size-6 text-destructive" />;
      case "wrench":
        return <Wrench className="size-6 text-destructive" />;
      default:
        return <AlertTriangle className="size-6 text-destructive" />;
    }
  };
  return <section id="emergencyservicesfeature" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Top Status & Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-destructive/10 border border-destructive/20 text-destructive text-sm font-semibold mb-4">
            <span className="relative flex size-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
              <span className="relative inline-flex rounded-full size-2.5 bg-destructive"></span>
            </span>
            <span>24/7 Jonesboro Emergency Triage</span>
          </div>

          <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
            {" Need Emergency Plumbing?"}
          </h2>

          <p className="text-muted-foreground text-base md:text-lg max-w-2xl">
            Plumbing disasters cause thousands in water damage every minute. Select your emergency below for immediate triage instructions and direct on-call plumber dispatch in Clayton County.
          </p>
        </div>

        {/* 3-Column Triage Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {emergencyItems.map((item, index) => {
          return <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: index * 0.05
          }} className="h-full flex">
                <Card className="flex-1 flex flex-col justify-between border-border hover:border-destructive/40 transition-colors shadow-sm bg-card text-card-foreground">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="p-3 rounded-lg bg-destructive/10 flex items-center justify-center">
                        {renderIcon(item.icon)}
                      </div>
                      {getUrgencyBadge(item.urgencyLevel)}
                    </div>
                    <CardTitle className="text-xl font-bold tracking-tight font-default">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm pt-1 leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="pt-4 border-t border-border/60 flex items-center justify-between text-xs font-medium text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Clock className="size-3.5 text-destructive" />
                        <span>{item.urgency}</span>
                      </div>
                      <Link to="/emergency-plumbing/" className="text-primary hover:text-primary/80 font-semibold inline-flex items-center gap-1 group">
                        <span>Details</span>
                        <ArrowRight className="size-3 transition-transform group-hover:translate-x-0.5" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* High-Contrast Emergency Action Banner */}
        <div className="rounded-2xl bg-primary text-primary-foreground p-8 md:p-12 shadow-xl border border-border/20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-3 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 text-accent font-semibold text-sm">
                <AlertTriangle className="size-4" />
                <span>On-Call Licensed Techs Ready in Jonesboro</span>
              </div>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold font-default tracking-tight text-primary-foreground">
                Active Leak or Flooding Right Now?
              </h3>
              <p className="text-primary-foreground/90 text-sm md:text-base max-w-xl">
                Do not wait for water to soak through drywall and subfloors. Call our 24/7 direct dispatch line now or submit an immediate priority routing request.
              </p>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-4 justify-center lg:justify-end">
              <Button asChild size="lg" className="bg-destructive text-white hover:bg-destructive/90 text-base font-bold shadow-md h-12 px-6">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-5" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 border-transparent text-base font-semibold h-12 px-6">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <span>Dispatch Request</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}